// src/pages/admin/library/AddBookModal.tsx
import React, { useEffect, useState } from 'react';
import { 
  FaTimes, 
  FaSave, 
  FaBook, 
  FaImage, 
  FaSpinner, 
  FaBarcode,
  FaPen,
  FaUserEdit,
  FaList,
  FaCopy,
  FaTrash
} from '../../../utils/iconUtils';
import { LibraryCategory, getLibraryCategories } from '../../../firebase/libraryFirestoreService';
import { toast } from 'react-hot-toast';

interface AddBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (book: BookFormData) => Promise<void>;
  initialData?: Partial<BookForm>;
  loading?: boolean;
  categories?: LibraryCategory[];
}

interface BookForm {
  isbn: string;
  title: string;
  author: string;
  categoryId: string;
  totalCopies: number;
  imageUrl?: string;
  imageFile?: File | null;
}

interface BookFormData {
  isbn: string;
  title: string;
  author: string;
  categoryId: string;
  totalCopies: number;
  imageUrl?: string;
  imageFile?: File | null;
}

const AddBookModal: React.FC<AddBookModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  loading = false,
  categories: propCategories
}) => {
  const [formData, setFormData] = useState<BookForm>({
    isbn: '',
    title: '',
    author: '',
    categoryId: '',
    totalCopies: 1,
  });

  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setFormData({
        isbn: initialData.isbn || '',
        title: initialData.title || '',
        author: initialData.author || '',
        categoryId: initialData.categoryId || '',
        totalCopies: initialData.totalCopies || 1,
        imageUrl: initialData.imageUrl || ''
      });
      if (initialData.imageUrl) setImagePreview(initialData.imageUrl);
    } else {
      setFormData({ isbn: '', title: '', author: '', categoryId: '', totalCopies: 1 });
      setImagePreview(null);
    }

    if (propCategories && Array.isArray(propCategories) && propCategories.length > 0) {
      setCategories(propCategories);
    } else {
      fetchCategories();
    }
  }, [isOpen, initialData, propCategories]);

  const fetchCategories = async () => {
    try {
      const result = await getLibraryCategories();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'totalCopies') {
        return { ...prev, totalCopies: Math.max(1, Number(value)) };
      }
      return { ...prev, [name]: value } as unknown as BookForm;
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      setFormData(prev => ({ ...prev, imageFile: file, imageUrl: '' }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imageFile: null, imageUrl: '' }));
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.isbn.trim()) {
      toast.error('ISBN is required');
      return;
    }
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.categoryId) {
      toast.error('Please select a category');
      return;
    }

    if (formData.totalCopies < 1) {
      toast.error('Total copies must be at least 1');
      return;
    }

    await onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {initialData ? 'Edit Book' : 'Add New Book'}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {initialData ? 'Update book details' : 'Add a new book to library'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <FaTimes className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* ISBN & Title */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaBarcode className="inline-block h-4 w-4 mr-2 text-primary-600" />
                ISBN *
              </label>
              <input
                type="text"
                name="isbn"
                value={formData.isbn}
                onChange={handleChange}
                required
                placeholder="978-0-123456-78-9"
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaBook className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Book title"
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              />
            </div>
          </div>

          {/* Author & Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaUserEdit className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Author
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaList className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Category *
              </label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Book Cover Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaImage className="inline-block h-4 w-4 mr-2 text-primary-600" />
              Book Cover
            </label>
            <div className="flex items-start gap-4">
              <div>
                {imagePreview ? (
                  <div className="flex flex-col items-start">
                    <div className="w-32 h-40 overflow-hidden rounded-lg border border-gray-300 bg-white flex items-center justify-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                        disabled={loading}
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-32 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    <FaImage className="h-10 w-10 text-gray-400" />
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="mb-3">
                  <label className="cursor-pointer">
                    <div className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                      <FaImage className="h-4 w-4" />
                      Upload Image
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      disabled={loading}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Max 5MB • JPG, PNG, WebP
                  </p>
                </div>

                <div className="mt-2 ml-auto flex flex-col items-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="h-4 w-4" />
                        {initialData ? 'Update Book' : 'Save Book'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Copies */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCopy className="inline-block h-4 w-4 mr-2 text-primary-600" />
              Total Copies *
            </label>
            <input
              type="number"
              name="totalCopies"
              value={formData.totalCopies}
              onChange={handleChange}
              min="1"
              required
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Quick Tips */}
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
            <p className="font-medium mb-1">📚 Quick Tips:</p>
            <ul className="space-y-1">
              <li>• ISBN must be unique for each book</li>
              <li>• Category helps in organizing books</li>
              <li>• Image size limit: 5MB</li>
            </ul>
          </div>

          
        </form>
      </div>
    </div>
  );
};

export default AddBookModal;